import { FormArray, FormControl } from '@angular/forms';

export type SeuConfigItemType =
  | SeuConfigItemTypeText
  | 'INPUT'
  | 'LIST'
  | 'ACCORDION'
  | 'RADIO_GROUP'
  | 'TOGGLE'
  | 'SEPARATOR';

export type SeuConfigItemTypeText = 'TITLE' | 'SUBTITLE';

export type SeuConfigInputType =
  | 'text'
  | 'number'
  | 'select'
  | 'multi-select'
  | 'textarea'
  | 'search';

export type SeuConfigImportanceType = 'mandatory' | 'optional';

export type SeuConfigItem =
  | SeuConfigTitle
  | SeuConfigInput
  | SeuConfigList
  | SeuConfigRadioGroup
  | SeuConfigAccordion
  | SeuConfigToggle
  | SeuConfigSeparator;

export type SeuConfigItemOrFileRef = SeuConfigItem | SeuConfigFileRef;

type SeuConfigItemBase = {
  name: string;
  type: SeuConfigItemType;
  hideLabel?: boolean;
};

export type SeuConfigToggle = SeuConfigItemBase & {
  type: 'TOGGLE';
  toggled?: boolean;
  namePos?: 'left' | 'right';
  toggleItems?: SeuConfigItem[];
  tooltip?: string;
};

export type SeuConfigAccordion = SeuConfigItemBase & {
  type: 'ACCORDION';
  nameEditable?: boolean;
  collapsed?: boolean;
  accordionItems: SeuConfigItem[];
  tooltip?: string;
};

type SeuConfigSeparator = {
  name?: string;
  type: 'SEPARATOR';
};

export type SeuConfigFileRef = {
  configFile: string;
};

export type SeuConfigTitle = SeuConfigItemBase & {
  type: SeuConfigItemTypeText;
  tooltip?: string;
};

export type SeuConfigInput = SeuConfigItemBase & {
  type: 'INPUT';
  inputType: SeuConfigInputType;
  importance?: SeuConfigImportanceType;
  options?: string[];
  units?: string[];
  defaultUnit?: string;
  data?: string | string[];
  disabled?: boolean;
  default?: number | string | string[];
  tooltip?: string;
  hoverText?: string;
  groupUserInputs?: string;
};

export type SeuConfigListItemFormDefinition = {
  uid: string;
  items: SeuConfigItem[];
};

export type SeuConfigRadioItemFormDefinition = {
  key: string;
  items: SeuConfigItem[];
};

export type SeuConfigList = SeuConfigItemBase & {
  type: 'LIST';
  listItems: SeuConfigItem[];
  displayListItems?: 'accordion';
  initItemCount: number;
  maxItemCount: number;
  label?: string;
  importance?: SeuConfigImportanceType;
  tooltip?: string;
};

export type SeuConfigRadioGroupItem = {
  name: string;
  radioLabel?: string;
  checked?: boolean;
  disabled?: boolean;
  items?: SeuConfigItem[];
  tooltip?: string;
};

export type SeuConfigRadioGroup = SeuConfigItemBase & {
  type: 'RADIO_GROUP';
  radioItems: SeuConfigRadioGroupItem[];
  radioLabel?: string;
  disabled?: boolean;
  tooltip?: string;
};

export type SeuConfigFormDefinition = {
  items: SeuConfigItemOrFileRef[];
  splitterUnits?: string[];
  [key: string]: unknown;
};

export type SeuConfigFormDefinitions = {
  [key in string]?: SeuConfigFormDefinition;
};

export type SeuConfigSvgDefinitions = {
  [key: string]: string;
};

export type SeuConfig = {
  formDefinitions: SeuConfigFormDefinitions;
  opexPresets?: SeuConfigPresets;
  limitsPresets?: SeuConfigPresets;
  svgs: SeuConfigSvgDefinitions;
  translations: {
    DE: SeuConfigTranslationObject;
    EN: SeuConfigTranslationObject;
  };
  hierarchy: string[];
  unitOrder?: string[];
  changedConfigs?: SeuChangedConfigs;
  versionNumber: number;
};

export type SeuConfigPresets = {
  [presetKey in string]: SeuConfigPresetFields;
};

export type SeuConfigPresetFields = {
  [fieldKey in string]: SeuConfigPresetField;
};

export type SeuConfigPresetField = {
  unit: string;
  value: number;
};

export type SeuConfigPresetConfig = {
  presets: SeuConfigPresets;
  presetFieldKey: string;
};

export type SeuConfigUnitOrder = {
  unitOrder: string[];
};

export type SeuConfigVersion = {
  versionNr: number;
  changedElements: string[];
};

export type SeuChangedConfigVersions = {
  components: string[];
  globals: string[];
};

export type SeuChangedConfigs = Record<string, SeuChangedConfigVersions>;

export type SeuConfigApiValue =
  | SeuConfigFormDefinition
  | SeuConfigTranslationObject
  | SeuConfigPresets
  | SeuConfigUnitOrder
  | SeuChangedConfigs
  | string
  | number;

/**
 * Typed representation of the raw API response for a component config bundle.
 *
 * Key patterns:
 *   `${string}.json`  – component form definitions, translation files, and
 *                        known preset/util JSON blobs (see explicit overrides below)
 *   `${string}.svg`   – inline SVG strings keyed by component name
 *   `hierarchy`       – newline-separated component hierarchy string
 *
 * Explicit overrides refine the broad template index for well-known keys.
 */
export type SeuConfigApi = {
  /** Dynamic component form definitions (e.g. 'fan.json', 'bagFilter.json') */
  [key: `${string}.json`]:
    | SeuConfigFormDefinition
    | SeuConfigTranslationObject
    | SeuConfigPresets
    | SeuConfigUnitOrder
    | SeuChangedConfigs;
} & {
  /** Inline SVG content keyed by component name (e.g. 'fan.svg') */
  [key: `${string}.svg`]: string;
} & {
  /** Newline-separated hierarchy string */
  hierarchy?: string;
  // --- well-known JSON overrides ---
  'DE.json'?: SeuConfigTranslationObject;
  'EN.json'?: SeuConfigTranslationObject;
  'opexPresets.json'?: SeuConfigPresets;
  'limitsPresets.json'?: SeuConfigPresets;
  'unitOrder.json'?: SeuConfigUnitOrder;
  'changedConfigs.json'?: SeuChangedConfigs;
  versionNumber: number;
};

export type SeuConfigInputValue = number | string | string[] | boolean | null;

export type SeuConfigInputItemUnitValue = {
  unit?: string;
  value: SeuConfigInputValue;
};

export type SeuConfigInputValueType = 'string' | 'number' | 'bool' | 'multi';

export type SeuConfigInputItem = {
  inputId: string;
  name: string;
  excludeFromChangelog?: boolean;
  unitValue: SeuConfigInputItemUnitValue;
  valueType: SeuConfigInputValueType;
};

export type SeuConfigTranslationObject = {
  [key in string]: SeuConfigTranslationValue;
};

/**
 * Typed representation of a single component entry in DE.json / EN.json.
 * Each component key (e.g. 'bagFilter', 'fan') maps to this shape.
 * `synonyms` may be a bare string or an array — always normalise to string[]
 * at read time (e.g. via SeuConfigSynonymsPipe).
 */
export type SeuConfigComponentTranslation = {
  componentName: string;
  form: SeuConfigTranslationObject;
  solutions: SeuConfigTranslationObject;
  options: SeuConfigTranslationObject;
  synonyms: string | string[];
};

export function isSeuConfigComponentTranslation(
  value: unknown
): value is SeuConfigComponentTranslation {
  return (
    typeof value === 'object' && value !== null && 'componentName' in value
  );
}

export type SeuConfigTranslationValue =
  | string
  | number
  | null
  | string[]
  | SeuConfigTranslationSelectOption[]
  | SeuConfigTranslationObject
  | SeuConfigTranslationObject[];

export type SeuConfigTranslationSelectOption = {
  pythonName: string;
  displayName: string;
  synonyms: string[];
};

export type SeuConfigFormGroupObjectEntry = {
  key: string;
  value: FormControl | FormArray | null;
  type?: string;
};
